
import { useCallback, useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { InterviewRequestsFacade } from '@/services/interview';
import { InterviewRequestResponse } from '@/services/interview/requests/types';

export const useInterviewRequests = () => {
  // Track deleted IDs locally to filter them out even before refetch completes
  const [deletedIds, setDeletedIds] = useState<Set<string>>(new Set());
  
  const { 
    data: interviewRequests, 
    isLoading, 
    error, 
    refetch, 
    isError
  } = useQuery<InterviewRequestResponse[]>({
    queryKey: ['interviewRequests'],
    queryFn: async () => {
      console.log('Fetching interview requests...');
      const result = await InterviewRequestsFacade.getAllInterviewRequests();
      console.log(`Fetch complete: ${result?.length || 0} requests returned`);
      return result || [];
    },
    initialData: [],
    staleTime: 30000, // Cache for 30 seconds
    refetchOnWindowFocus: true,
    refetchInterval: 60000, // Refetch every minute
    retry: (failureCount, error) => {
      // Only retry on network errors, not on 4xx errors
      if (error && 'status' in error && typeof error.status === 'number') {
        return error.status >= 500 && failureCount < 3;
      }
      return failureCount < 3;
    },
    retryDelay: (attempt) => Math.min(attempt * 1000, 3000),
  });

  // Memoized filtered requests to avoid unnecessary re-renders
  const filteredRequests = useMemo(() => {
    if (!interviewRequests) return [];
    return interviewRequests.filter(req => !deletedIds.has(req.id));
  }, [interviewRequests, deletedIds]);

  // Memoized refetch function
  const handleRefresh = useCallback(() => {
    // Clear deleted IDs set on manual refresh
    setDeletedIds(new Set());
    console.log("Manual refresh triggered by user");
    refetch();
  }, [refetch]);

  const handleDeleteSuccess = useCallback((deletedId: string) => {
    if (!deletedId) {
      console.error('Invalid deleted ID provided');
      return;
    }

    console.log(`Adding ID ${deletedId} to locally deleted set`);
    setDeletedIds(prev => new Set([...prev, deletedId]));
    
    // Delayed refetch to ensure database consistency
    setTimeout(() => {
      console.log(`Triggering delayed refetch after deletion of ${deletedId}`);
      refetch();
    }, 1000);
  }, [refetch]);

  // Effect for logging data changes
  useEffect(() => {
    if (interviewRequests) {
      console.log(`Data updated: ${interviewRequests.length} requests received from server`);
    }
    
    if (isError && error) {
      console.error('Error in interview requests query:', error);
    }
  }, [interviewRequests, isError, error]);

  return {
    interviewRequests: filteredRequests,
    isLoading,
    error: error as Error | null,
    refetch,
    isError,
    handleRefresh,
    handleDeleteSuccess,
    deletedIds,
  };
};
